import { create } from 'zustand'
import {
  listThreads,
  type ListThreadsResponse,
  type ThreadStatusSummary,
  type ThreadSummary,
} from '../api/threads'
import useMessagesStore from './messages'
import type { IncomingMessage } from './messages'
import useUnreadStore from './unread'

export interface ThreadFilters {
  unreadOnly: boolean
  flaggedOnly: boolean
  onAirOnly: boolean
  platforms: string[]
  intents: string[]
  q: string
}

export interface ThreadsState {
  stationId: number | null
  items: ThreadSummary[]
  filters: ThreadFilters
  defaultPlatforms: string[]
  cursor: number | null
  fetching: boolean
  totalUnreadThreads: number
  hydrate: (stationId: number, options?: { includeBlocked?: boolean }) => Promise<ListThreadsResponse | null>
  fetchMore: (options?: { includeBlocked?: boolean }) => Promise<ListThreadsResponse | null>
  upsertFromMessage: (message: IncomingMessage) => void
  setFilter: <K extends keyof ThreadFilters>(key: K, value: ThreadFilters[K]) => void
  resetFilters: () => void
  setDefaultPlatforms: (platforms: string[]) => void
  setThreadUnreadCount: (contactId: number, unreadCount: number) => void
  recomputeUnread: (contactId: number) => void
  recomputeThreadStatus: (contactId: number) => void
  applyThreadStatus: (contactId: number, summary: ThreadStatusSummary | Partial<ThreadSummary>) => void
  reset: () => void
}

const DEFAULT_PLATFORMS = ['email', 'telegram']

const dedupePlatforms = (platforms: string[] = []): string[] => {
  const cleaned = platforms
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
  return Array.from(new Set(cleaned))
}

const resolveDefaultPlatforms = (platforms: string[] = DEFAULT_PLATFORMS): string[] => {
  const deduped = dedupePlatforms(platforms)
  return deduped.length ? deduped : [...DEFAULT_PLATFORMS]
}

const createInitialFilters = (platforms: string[] = DEFAULT_PLATFORMS): ThreadFilters => ({
  unreadOnly: false,
  flaggedOnly: false,
  onAirOnly: false,
  platforms: resolveDefaultPlatforms(platforms),
  intents: [],
  q: '',
});

const mergeThreads = (existing: ThreadSummary[], incoming: ThreadSummary[]): ThreadSummary[] => {
  const map = new Map<number, ThreadSummary>()

  existing.forEach((item) => {
    const normalized = normalizeThreadSummary(item)
    map.set(normalized.contact_id, normalized)
  })

  incoming.forEach((item) => {
    const normalized = normalizeThreadSummary(item)
    const current = map.get(normalized.contact_id)
    if (!current) {
      map.set(normalized.contact_id, normalized)
      return
    }
    if (normalized.latest_message_id >= current.latest_message_id) {
      map.set(normalized.contact_id, mergeThreadStatus(normalized, current))
    } else {
      map.set(normalized.contact_id, mergeThreadStatus(current, normalized))
    }
  })

  return Array.from(map.values()).sort((a, b) => b.latest_message_id - a.latest_message_id)
}

const computeTotalUnreadThreads = (items: ThreadSummary[]): number => {
  return items.reduce((total, item) => total + (item.unread_count > 0 ? 1 : 0), 0)
}

const initialFilters: ThreadFilters = createInitialFilters()

const WATCHDOG_DELAY_MS = 2000

const coerceStatusId = (value?: number | null): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  return null
}

const coerceEpochSeconds = (value?: number | string | null): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return null
}

const resolveBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value === 1
  }
  return fallback
}

const normalizeThreadSummary = (thread: ThreadSummary): ThreadSummary => {
  const hasFlagged = resolveBoolean(thread.hasFlagged ?? thread.has_flagged, Boolean(thread.has_flagged))
  const hasOnAir = resolveBoolean(thread.hasOnAir ?? thread.has_on_air, Boolean(thread.has_on_air))
  const latestFlagged = coerceStatusId(
    thread.latestFlaggedMessageId ?? thread.latest_flagged_message_id ?? null,
  )
  const latestOnAir = coerceStatusId(
    thread.latestOnAirMessageId ?? thread.latest_on_air_message_id ?? null,
  )
  const latestIsAuto = resolveBoolean(
    thread.latestIsAutoresponder ?? thread.latest_is_autoresponder,
    Boolean(thread.latest_is_autoresponder ?? thread.latestIsAutoresponder ?? false),
  )
  const latestAutoRuleId = coerceStatusId(
    thread.latestAutoRuleId ?? thread.latest_auto_rule_id ?? null,
  )
  const rawAutoRuleName =
    thread.latestAutoRuleName ?? thread.latest_auto_rule_name ?? (latestIsAuto ? '' : null)
  const latestAutoRuleName =
    typeof rawAutoRuleName === 'string' && rawAutoRuleName.trim().length > 0
      ? rawAutoRuleName.trim()
      : null
  const latestIso =
    typeof thread.latest_received_at_iso_utc === 'string' && thread.latest_received_at_iso_utc.trim().length > 0
      ? thread.latest_received_at_iso_utc.trim()
      : null
  const latestEpoch = coerceEpochSeconds(
    (thread as { latestReceivedAtEpoch?: number | string | null }).latestReceivedAtEpoch ??
      thread.latest_received_at_epoch ??
      null,
  )

  return {
    ...thread,
    has_flagged: hasFlagged,
    has_on_air: hasOnAir,
    hasFlagged: hasFlagged,
    hasOnAir: hasOnAir,
    latest_flagged_message_id: latestFlagged,
    latestFlaggedMessageId: latestFlagged,
    latest_on_air_message_id: latestOnAir,
    latestOnAirMessageId: latestOnAir,
    latest_is_autoresponder: latestIsAuto,
    latestIsAutoresponder: latestIsAuto,
    latest_auto_rule_id: latestAutoRuleId,
    latestAutoRuleId: latestAutoRuleId,
    latest_auto_rule_name: latestAutoRuleName,
    latestAutoRuleName: latestAutoRuleName,
    latest_received_at_iso_utc: latestIso,
    latest_received_at_epoch: latestEpoch,
  }
}

const mergeThreadStatus = (
  existing: ThreadSummary,
  updates: Partial<ThreadSummary> | ThreadStatusSummary | null | undefined,
): ThreadSummary => {
  if (!updates) {
    return normalizeThreadSummary(existing)
  }

  const hasFlagged = resolveBoolean(
    (updates as ThreadSummary).hasFlagged ?? (updates as ThreadSummary).has_flagged,
    existing.hasFlagged ?? existing.has_flagged ?? false,
  )
  const hasOnAir = resolveBoolean(
    (updates as ThreadSummary).hasOnAir ?? (updates as ThreadSummary).has_on_air,
    existing.hasOnAir ?? existing.has_on_air ?? false,
  )

  const latestFlagged = coerceStatusId(
    (updates as ThreadSummary).latestFlaggedMessageId ??
      (updates as ThreadSummary).latest_flagged_message_id ??
      existing.latestFlaggedMessageId ??
      existing.latest_flagged_message_id ??
      null,
  )
  const latestOnAir = coerceStatusId(
    (updates as ThreadSummary).latestOnAirMessageId ??
      (updates as ThreadSummary).latest_on_air_message_id ??
      existing.latestOnAirMessageId ??
      existing.latest_on_air_message_id ??
      null,
  )
  const latestIsAuto = resolveBoolean(
    (updates as ThreadSummary).latestIsAutoresponder ??
      (updates as ThreadSummary).latest_is_autoresponder,
    existing.latestIsAutoresponder ?? existing.latest_is_autoresponder ?? false,
  )
  const latestAutoRuleId = coerceStatusId(
    (updates as ThreadSummary).latestAutoRuleId ??
      (updates as ThreadSummary).latest_auto_rule_id ??
      existing.latestAutoRuleId ??
      existing.latest_auto_rule_id ??
      null,
  )
  const rawAutoRuleName =
    (updates as ThreadSummary).latestAutoRuleName ??
    (updates as ThreadSummary).latest_auto_rule_name ??
    existing.latestAutoRuleName ??
    existing.latest_auto_rule_name ??
    null
  const latestAutoRuleName =
    typeof rawAutoRuleName === 'string' && rawAutoRuleName.trim().length > 0
      ? rawAutoRuleName.trim()
      : null

  return normalizeThreadSummary({
    ...existing,
    ...(updates as Partial<ThreadSummary>),
    has_flagged: hasFlagged,
    has_on_air: hasOnAir,
    hasFlagged,
    hasOnAir,
    latest_flagged_message_id: latestFlagged,
    latestFlaggedMessageId: latestFlagged,
    latest_on_air_message_id: latestOnAir,
    latestOnAirMessageId: latestOnAir,
    latest_is_autoresponder: latestIsAuto,
    latestIsAutoresponder: latestIsAuto,
    latest_auto_rule_id: latestAutoRuleId,
    latestAutoRuleId: latestAutoRuleId,
    latest_auto_rule_name: latestAutoRuleName,
    latestAutoRuleName: latestAutoRuleName,
  })
}

const useThreadsStore = create<ThreadsState>((set, get) => {
  let hydrateController: AbortController | null = null
  let hydrateWatchdog: ReturnType<typeof setTimeout> | null = null
  let hasWatchdogRetried = false
  let lastHydrateOptions: { stationId: number; includeBlocked?: boolean } | null = null

  const clearWatchdog = () => {
    if (hydrateWatchdog) {
      clearTimeout(hydrateWatchdog)
      hydrateWatchdog = null
    }
  }

  const finalizeController = (controller: AbortController) => {
    if (hydrateController === controller) {
      hydrateController = null
    }
  }

  const scheduleWatchdog = () => {
    if (typeof window === 'undefined') {
      return
    }

    clearWatchdog()

    if (!lastHydrateOptions) {
      return
    }

    hydrateWatchdog = window.setTimeout(() => {
      const state = get()
      if (state.fetching || hasWatchdogRetried || state.items.length > 0) {
        return
      }

      if (!lastHydrateOptions) {
        return
      }

      hasWatchdogRetried = true

      if (window.__RPA_DEBUG?.net) {
        console.log('[threads] watchdog retry', lastHydrateOptions)
      }

      void runHydrate(lastHydrateOptions.stationId, lastHydrateOptions.includeBlocked, true)
    }, WATCHDOG_DELAY_MS)
  }

  async function runHydrate(
    stationId: number,
    includeBlocked?: boolean,
    isRetry = false,
  ): Promise<ListThreadsResponse | null> {
    const filters = get().filters

    if (!isRetry) {
      hasWatchdogRetried = false
    }

    lastHydrateOptions = { stationId, includeBlocked }
    clearWatchdog()

    const controller = new AbortController()
    if (hydrateController) {
      hydrateController.abort()
    }
    hydrateController = controller

    set({ fetching: true, stationId, cursor: null })

    try {
      const response = await listThreads(
        {
          stationId,
          limit: 50,
          platforms: filters.platforms,
          intents: filters.intents,
          unreadOnly: filters.unreadOnly,
          flaggedOnly: filters.flaggedOnly,
          onAirOnly: filters.onAirOnly,
          search: filters.q,
          includeBlocked,
        },
        controller.signal,
      )

      if (controller.signal.aborted) {
        return null
      }

      const items = [...(response.items ?? [])]
        .map(normalizeThreadSummary)
        .sort((a, b) => b.latest_message_id - a.latest_message_id)
      const totalUnreadThreads = response.total_unread_threads ?? computeTotalUnreadThreads(items)

      set({
        items,
        cursor: response.next_cursor ?? null,
        fetching: false,
        totalUnreadThreads,
      })

      const threadCounts: Record<string, number> = {}
      items.forEach((item) => {
        threadCounts[String(item.contact_id)] = Math.max(0, item.unread_count ?? 0)
      })
      useUnreadStore.getState().hydrateThreads(threadCounts)

      scheduleWatchdog()

      return response
    } catch (error) {
      if (controller.signal.aborted) {
        return null
      }

      set({ fetching: false })
      throw error
    } finally {
      finalizeController(controller)
    }
  }

  return {
    stationId: null,
    items: [],
    filters: initialFilters,
    defaultPlatforms: [...initialFilters.platforms],
    cursor: null,
    fetching: false,
    totalUnreadThreads: 0,
    async hydrate(stationId, options) {
      return runHydrate(stationId, options?.includeBlocked)
    },
  async fetchMore(options) {
    const state = get()

    if (state.fetching || !state.stationId || !state.cursor) {
      return null
    }

    set({ fetching: true })

    try {
      const response = await listThreads({
        stationId: state.stationId,
        cursor: state.cursor,
        dir: 'older',
        limit: 50,
        platforms: state.filters.platforms,
        intents: state.filters.intents,
        unreadOnly: state.filters.unreadOnly,
        flaggedOnly: state.filters.flaggedOnly,
        onAirOnly: state.filters.onAirOnly,
        search: state.filters.q,
        includeBlocked: options?.includeBlocked,
      })

      const merged = mergeThreads(state.items, (response.items ?? []).map(normalizeThreadSummary))
      const totalUnreadThreads = response.total_unread_threads ?? computeTotalUnreadThreads(merged)

      set({
        items: merged,
        cursor: response.next_cursor ?? null,
        fetching: false,
        totalUnreadThreads,
      })

      const threadCounts: Record<string, number> = {}
      merged.forEach((item) => {
        threadCounts[String(item.contact_id)] = Math.max(0, item.unread_count ?? 0)
      })
      useUnreadStore.getState().hydrateThreads(threadCounts)

      return response
    } catch (error) {
      set({ fetching: false })
      throw error
    }
  },
  upsertFromMessage(message) {
    let unreadDelta = 0
    let targetContactId = 0
    set((state) => {
      const contactId = message.contact_id || message.contact?.id || 0
      if (!contactId) {
        return state
      }
      targetContactId = contactId

      const items = [...state.items]
      const index = items.findIndex((item) => item.contact_id === contactId)
      const identityId = message.contact_identity_id || message.identity?.id || 0
      const identityPlatform = message.identity?.platform || message.platform || ''
      const identityName = message.identity?.display_name || ''
      const latestReceivedAt = message.received_at || new Date().toISOString()
      const latestReceivedAtIso =
        typeof message.received_at_iso_utc === 'string' && message.received_at_iso_utc.trim().length > 0
          ? message.received_at_iso_utc.trim()
          : null
      const latestReceivedAtEpoch = coerceEpochSeconds(message.received_at_epoch ?? null)
      const latestContent = message.content ?? ''
      const platform = message.platform || identityPlatform
      const contactName = message.contact?.name || identityName || 'Unknown'
      const isAutoresponder = Boolean(message.is_autoresponder)
      const autoRuleId = message.auto_rule_id ?? null
      const autoRuleName =
        typeof message.auto_rule_name === 'string' && message.auto_rule_name.trim().length > 0
          ? message.auto_rule_name.trim()
          : null

      if (index === -1) {
        const identities = identityId
          ? [
              {
                id: identityId,
                platform: identityPlatform,
                display_name: identityName,
              },
            ]
          : []

        const isRead = Boolean(message.is_read)
        const unreadCount = isRead ? 0 : 1
        if (!isRead) {
          unreadDelta += 1
        }

        const baseThread: ThreadSummary = {
          contact_id: contactId,
          contact_name: contactName,
          latest_message_id: message.id,
          latest_content: latestContent,
          latest_platform: platform,
          latest_received_at: latestReceivedAt,
          latest_received_at_iso_utc: latestReceivedAtIso,
          latest_received_at_epoch: latestReceivedAtEpoch,
          unread_count: unreadCount,
          has_flagged: Boolean(message.is_flagged),
          has_on_air: Boolean(message.is_on_air),
          hasFlagged: Boolean(message.is_flagged),
          hasOnAir: Boolean(message.is_on_air),
          latest_flagged_message_id: message.is_flagged ? message.id : null,
          latestFlaggedMessageId: message.is_flagged ? message.id : null,
          latest_on_air_message_id: message.is_on_air ? message.id : null,
          latestOnAirMessageId: message.is_on_air ? message.id : null,
          latest_is_autoresponder: isAutoresponder,
          latestIsAutoresponder: isAutoresponder,
          latest_auto_rule_id: autoRuleId,
          latestAutoRuleId: autoRuleId,
          latest_auto_rule_name: autoRuleName,
          latestAutoRuleName: autoRuleName,
          identities,
        }

        const nextItems = [normalizeThreadSummary(baseThread), ...items]

        nextItems.sort((a, b) => b.latest_message_id - a.latest_message_id)

        return {
          ...state,
          items: nextItems,
          totalUnreadThreads: computeTotalUnreadThreads(nextItems),
        }
      }

      const existing = items[index]
      const isNewer = message.id > existing.latest_message_id
      const nextIdentities = [...existing.identities]

      if (identityId && !nextIdentities.some((identity) => identity.id === identityId)) {
        nextIdentities.push({
          id: identityId,
          platform: identityPlatform,
          display_name: identityName,
        })
      }

      let unreadCount = existing.unread_count
      const isRead = Boolean(message.is_read)

      if (!isRead && message.id > existing.latest_message_id) {
        unreadCount += 1
        unreadDelta += 1
      }

      const statusUpdates: Partial<ThreadSummary> = {}
      if (message.is_flagged) {
        statusUpdates.has_flagged = true
        statusUpdates.hasFlagged = true
        statusUpdates.latest_flagged_message_id = message.id
        statusUpdates.latestFlaggedMessageId = message.id
      }
      if (message.is_on_air) {
        statusUpdates.has_on_air = true
        statusUpdates.hasOnAir = true
        statusUpdates.latest_on_air_message_id = message.id
        statusUpdates.latestOnAirMessageId = message.id
      }

      let merged = mergeThreadStatus(existing, statusUpdates)

      merged = {
        ...merged,
        latest_message_id: isNewer ? message.id : merged.latest_message_id,
        latest_content: isNewer ? latestContent : merged.latest_content,
        latest_platform: isNewer ? platform : merged.latest_platform,
        latest_received_at: isNewer ? latestReceivedAt : merged.latest_received_at,
        latest_received_at_iso_utc:
          isNewer && latestReceivedAtIso ? latestReceivedAtIso : merged.latest_received_at_iso_utc,
        latest_received_at_epoch:
          isNewer && latestReceivedAtEpoch !== null
            ? latestReceivedAtEpoch
            : merged.latest_received_at_epoch,
        contact_name: contactName || merged.contact_name,
        unread_count: unreadCount,
        identities: nextIdentities,
      }

      if (isNewer) {
        merged = {
          ...merged,
          latest_is_autoresponder: isAutoresponder,
          latestIsAutoresponder: isAutoresponder,
          latest_auto_rule_id: autoRuleId,
          latestAutoRuleId: autoRuleId,
          latest_auto_rule_name: autoRuleName,
          latestAutoRuleName: autoRuleName,
        }
      }

      items[index] = normalizeThreadSummary(merged)
      items.sort((a, b) => b.latest_message_id - a.latest_message_id)

      return {
        ...state,
        items,
        totalUnreadThreads: computeTotalUnreadThreads(items),
      }
    })

    if (unreadDelta !== 0 && targetContactId > 0) {
      useUnreadStore.getState().applyNewMessage(targetContactId, unreadDelta)
    }
  },
  setFilter: (key, value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value,
      },
      cursor: null,
    }))
  },
  resetFilters: () => {
    set((state) => ({
      filters: createInitialFilters(state.defaultPlatforms),
      cursor: null,
    }))
  },
  setDefaultPlatforms: (platforms) => {
    const normalized = resolveDefaultPlatforms(platforms)
    set((state) => ({
      defaultPlatforms: normalized,
      filters: { ...state.filters, platforms: normalized },
    }))
  },
  setThreadUnreadCount: (contactId, unreadCount) => {
    const normalized = Math.max(0, unreadCount)
    set((state) => {
      const items = state.items.map((item) =>
        item.contact_id === contactId ? { ...item, unread_count: normalized } : item,
      )

      return {
        ...state,
        items,
        totalUnreadThreads: computeTotalUnreadThreads(items),
      }
    })
    useUnreadStore.getState().applyThreadReadUpTo(contactId, normalized)
  },
  recomputeThreadStatus: (contactId) => {
    set((state) => {
      const index = state.items.findIndex((item) => item.contact_id === contactId)
      if (index === -1) {
        return state
      }

      const messagesState = useMessagesStore.getState()
      const threadMessages = messagesState.byContact?.[contactId]?.items ?? []
      const flaggedIds = threadMessages.filter((message) => Boolean(message.is_flagged)).map((msg) => msg.id)
      const onAirIds = threadMessages.filter((message) => Boolean(message.is_on_air)).map((msg) => msg.id)

      const hasFlagged = flaggedIds.length > 0
      const hasOnAir = onAirIds.length > 0
      const latestFlagged = flaggedIds.length ? Math.max(...flaggedIds) : null
      const latestOnAir = onAirIds.length ? Math.max(...onAirIds) : null

      const target = state.items[index]
      const merged = mergeThreadStatus(target, {
        has_flagged: hasFlagged,
        has_on_air: hasOnAir,
        latest_flagged_message_id: latestFlagged,
        latest_on_air_message_id: latestOnAir,
      })

      if (
        target.has_flagged === merged.has_flagged &&
        target.has_on_air === merged.has_on_air &&
        (target.latest_flagged_message_id ?? null) === (merged.latest_flagged_message_id ?? null) &&
        (target.latest_on_air_message_id ?? null) === (merged.latest_on_air_message_id ?? null)
      ) {
        return state
      }

      const items = [...state.items]
      items[index] = merged

      return {
        ...state,
        items,
      }
    })
  },
  applyThreadStatus: (contactId, summary) => {
    set((state) => {
      const index = state.items.findIndex((item) => item.contact_id === contactId)
      if (index === -1) {
        return state
      }

      const target = state.items[index]
      const merged = mergeThreadStatus(target, summary)

      if (
        target.has_flagged === merged.has_flagged &&
        target.has_on_air === merged.has_on_air &&
        (target.latest_flagged_message_id ?? null) === (merged.latest_flagged_message_id ?? null) &&
        (target.latest_on_air_message_id ?? null) === (merged.latest_on_air_message_id ?? null)
      ) {
        return state
      }

      const items = [...state.items]
      items[index] = merged

      return {
        ...state,
        items,
      }
    })
  },
  recomputeUnread: (contactId) => {
    let normalizedUnread: number | null = null
    set((state) => {
      if (!contactId) {
        return state
      }

      const target = state.items.find((item) => item.contact_id === contactId)
      if (!target) {
        return state
      }

      const messagesState = useMessagesStore.getState()
      const cachedThread = messagesState.byContact?.[contactId]
      if (!cachedThread) {
        return state
      }

      const unread = cachedThread.items.reduce((total: number, message) => {
        return total + (message.is_read ? 0 : 1)
      }, 0)

      normalizedUnread = Math.max(0, unread)
      if (target.unread_count === normalizedUnread) {
        return state
      }

      const items = state.items.map((item) =>
        item.contact_id === contactId ? { ...item, unread_count: normalizedUnread ?? 0 } : item,
      )

      return {
        ...state,
        items,
        totalUnreadThreads: computeTotalUnreadThreads(items),
      }
    })

    if (normalizedUnread !== null) {
      useUnreadStore.getState().applyThreadReadUpTo(contactId, normalizedUnread)
    }
  },
  reset: () => {
    if (hydrateController) {
      hydrateController.abort()
      hydrateController = null
    }
    hasWatchdogRetried = false
    lastHydrateOptions = null
    clearWatchdog()
    set({
      stationId: null,
      items: [],
      filters: createInitialFilters(),
      defaultPlatforms: resolveDefaultPlatforms(),
      cursor: null,
      fetching: false,
      totalUnreadThreads: 0,
    })
    useUnreadStore.getState().reset()
  },
}
});

export const isDefaultFilters = (filters: ThreadFilters, defaults: string[]): boolean => {
  const normalizedDefaults = resolveDefaultPlatforms(defaults)
  return (
    !filters.unreadOnly &&
    !filters.flaggedOnly &&
    !filters.onAirOnly &&
    filters.intents.length === 0 &&
    filters.q.trim() === '' &&
    filters.platforms.length === normalizedDefaults.length &&
    filters.platforms.every((platform) => normalizedDefaults.includes(platform))
  )
}

export default useThreadsStore

if (typeof window !== 'undefined') {
  window.__RPA_DEBUG = {
    ...(window.__RPA_DEBUG ?? {}),
    store: {
      ...(window.__RPA_DEBUG?.store ?? {}),
      threads: useThreadsStore,
    },
  }
}
